<template>
    <canvas ref="refCanvas"
        :style="data.styleCanvas"
        class="no-select"
        :class="data.classCanvas"
        :width="data.canvasWidth"
        :height="data.canvasHeight"
    ></canvas>
</template>

<script>

    import {ref, reactive, onMounted, onUnmounted, watchEffect, computed, watch}  from "vue"
    import {usePageResize} from "@/modules/reader/book/pageResize";
    import {usePaging} from "@/modules/reader/book/paging";
    import {useTool} from "@/modules/reader/tools/tools";
    import config from "@/config/config.json";
    import * as freehandTool from "@/modules/reader/tools/drawing/freehandTool";
    import * as straightLineTool from "@/modules/reader/tools/drawing/straightLineTool";
    import * as arrowTool from "@/modules/reader/tools/drawing/arrowTool";
    import * as eraserTool from "@/modules/reader/tools/drawing/eraserTool";

    export default {
        components: {

        },
        props: {
            pagenum:Number
        },
        setup(props, context) {
            const refCanvas = ref(null);
            const { pageResizeState} = usePageResize();
            const { pageState } = usePaging();
            const {
                toolState,
                rightCanvasData,
                initCanvas,
                TOOL_BRUSH,
                TOOL_HIGHLIGHTER,
                TOOL_NOTE,
                TOOL_ERASER
            } = useTool();

            let ctx;

            /**
             Crucial setting! The canvas resolution cannot be set too large, otherwise, performance will be poor on tablet device,
             and canvas context can also be unable to obtain. The trick here is to limit the size, while maintaining max zoom that is still large
             enough to zoom in the page, by dividing the canvas size by 2 and applying CSS transform with scale multiplied by 2
             */
            const data = reactive({
                    styleCanvas: computed(()=>{
                        return {
                          // transform:`scale(${1/toolState.canvasScale * 2}, ${1/toolState.canvasScale * 2})`,
                            width: `${pageResizeState.fittedBrowserWidth}px`,
                            height: `${pageResizeState.fittedBrowserHeight}px`
                        }
                    }),
                    classCanvas: computed(()=>{
                        return {
                            "canvas-using": toolState.isUsing
                        }
                    }),
                    canvasWidth: computed(()=>{
                        return Math.round(pageResizeState.baseWidth * config.reader.maxZoom / 2)
                    }),
                    canvasHeight: computed(()=>{
                        return Math.round(pageResizeState.baseHeight * config.reader.maxZoom / 2);
                    })
                }
            )


            onMounted(()=>{
                ctx = refCanvas.value.getContext("2d");
                initCanvas(refCanvas.value);
            })

            const deactivateAllTools =()=> {
                if (refCanvas.value) {
                    freehandTool.deactivate(refCanvas.value.id);
                    straightLineTool.deactivate(refCanvas.value.id);
                    arrowTool.deactivate(refCanvas.value.id);
                    eraserTool.deactivate(refCanvas.value.id);
                }
            }

            const activateCurrentTool = ()=> {

                if (toolState.activeTool === TOOL_BRUSH) {
                    if (toolState.brushSetting.lineMode === config.reader.tool.brushModes.freehand) {
                        freehandTool.activate(refCanvas.value.id);
                    } else if (toolState.brushSetting.lineMode === config.reader.tool.brushModes.solidLine || toolState.brushSetting.lineMode === config.reader.tool.brushModes.dashLine) {
                        straightLineTool.activate(refCanvas.value.id);
                    } else if (toolState.brushSetting.lineMode === config.reader.tool.brushModes.arrow) {
                        arrowTool.activate(refCanvas.value.id)
                    }
                } else if (toolState.activeTool === TOOL_HIGHLIGHTER) {
                    straightLineTool.activate(refCanvas.value.id);
                } else if (toolState.activeTool === TOOL_ERASER) {
                    eraserTool.activate(refCanvas.value.id);
                }

            }

            const cloneFromCanvasData = ()=> {

                if (rightCanvasData.value[props.pagenum]) {
                    let destinationImage = new Image();
                    let url = URL.createObjectURL(rightCanvasData.value[props.pagenum]);

                    //TODO: use promise, await...
                    destinationImage.onload = ()=>{
                        //note: call clearRect first before drawImage; otherwise, eraser could be unable to preserve
                        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
                        ctx.drawImage(destinationImage,0,0);
                        URL.revokeObjectURL(url);
                        toolState.isRightPageUpdated = false;
                    }

                    destinationImage.src = url;
                }
            }


            watchEffect(()=>{

                if (!refCanvas.value) {
                    return;
                }

                if (toolState.isAllowed) {
                    if (pageState.pageMode === "single") {

                        if (pageState.currentPage[0] === props.pagenum &&  refCanvas.value.className.indexOf("canvas-left") >= 0) {
                            deactivateAllTools();
                            activateCurrentTool();
                        }

                    } else {

                        if (pageState.currentPage.includes(props.pagenum)) {
                            deactivateAllTools();
                            activateCurrentTool();
                        }
                    }

                } else {
                    deactivateAllTools();
                }


                //clone canvas if the active page is the 'right-hand-side' page, i.e. odd number if page counts from 1.
                if (toolState.isRightPageUpdated) {
                    if (pageState.isRightPageAvailable) {
                        //if double page mode, after drawing is done, clone to the corresponding "left-hand-side" canvas that appears in single page mode
                        if (refCanvas.value.className.indexOf("canvas-left") >= 0 &&  pageState.currentPage[1] === props.pagenum ) {
                            cloneFromCanvasData();
                        }
                    } else {
                        //if single page mode, after drawing is done, clone to the corresponding "right-hand-side" canvas that appears in double page mode.
                        if (refCanvas.value.className.indexOf("canvas-right") >= 0 && pageState.currentPage[0]  ===  props.pagenum) {
                            cloneFromCanvasData();
                        }
                    }
                }
            })

            return {
                toolState,
                rightCanvasData,
                pageState,
                pageResizeState,
                refCanvas,
                initCanvas,
                data
            }
        }
    }

</script>

<style scoped lang="scss">
    canvas {
        position:absolute;
        transform-origin:left top;
        top:0;
        z-index:$zindex-tool-canvas;
        //wdith:624px;
        //height:818px;
    }

    .canvas-using {
        z-index:#{$zindex-tool-canvas+2} !important;
    }

</style>